import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Drawer } from '@/components/ui/Drawer';
import { Badge, Button } from '@/components/ui/primitives';
import { ErrorState, LoadingState } from '@/components/DataState';
import { getBrandKit, saveBrandKit, getBrandLogo, uploadBrandLogo } from '@/data/sources-social';
import { BRAND_FONTS, fontStack, type FontCategory } from './brand-fonts';
import { ColorWheelPicker } from './ColorWheelPicker';
import './brand-fonts.css';
import type { BrandKit } from '@/types/social';

/* Brand Kit v0 (Drawer). Reads/writes bc_workspace_settings social.brand.* via
 * the brand-kit endpoint. Logo upload is LIVE (the G-CSV-2 owner gate is gone):
 * a PNG/JPEG is uploaded directly to /v1/social/brand-kit/logo and stored
 * workspace-scoped; a text-mark fallback shows when no logo is set. Colours use
 * a self-contained HSV wheel picker; fonts are a curated set of SELF-HOSTED
 * (OFL, same-origin — no Google CDN) families. Brand values auto-apply to every
 * template render. */

const HEX = /^#[0-9a-fA-F]{6}$/;
const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const areaCls = 'w-full rounded-md border border-border-default bg-canvas px-3 py-2 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30';
const inputCls = 'h-control w-full rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30';

const FONT_GROUPS: FontCategory[] = ['System', 'Sans', 'Serif', 'Display'];

function errMessage(e: unknown, fallback: string): string {
  const m = (e as { message?: string })?.message;
  return typeof m === 'string' && m.length > 0 ? m : fallback;
}

export function BrandKitPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['brand-kit'], queryFn: getBrandKit, enabled: open });
  const logoQ = useQuery({ queryKey: ['brand-logo'], queryFn: getBrandLogo, enabled: open });
  const [draft, setDraft] = useState<BrandKit | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [logoErr, setLogoErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (q.data) setDraft(q.data);
  }, [q.data]);

  const save = useMutation({
    mutationFn: (input: Partial<BrandKit>) => saveBrandKit(input),
    onSuccess: (res) => {
      setDraft(res);
      void qc.invalidateQueries({ queryKey: ['brand-kit'] });
    },
  });

  const upload = useMutation({
    mutationFn: (file: File) => uploadBrandLogo(file),
    onSuccess: () => {
      setLogoErr(null);
      void qc.invalidateQueries({ queryKey: ['brand-logo'] });
      void qc.invalidateQueries({ queryKey: ['brand-kit'] });
    },
    onError: (e) => setLogoErr(errMessage(e, 'Could not upload the logo. Try again.')),
  });

  const set = <K extends keyof BrandKit>(k: K, v: BrandKit[K]) => setDraft((d) => (d ? { ...d, [k]: v } : d));
  const toggleFont = (f: string) =>
    setDraft((d) => (d ? { ...d, fonts: d.fonts.includes(f) ? d.fonts.filter((x) => x !== f) : [...d.fonts, f] } : d));

  const onPickLogo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setLogoErr(null);
    if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
      setLogoErr('Please choose a PNG or JPEG image.');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setLogoErr('That image is larger than the 2 MB limit.');
      return;
    }
    upload.mutate(file);
  };

  const onSave = () => {
    if (!draft) return;
    setErr(null);
    if (draft.colorPrimary && !HEX.test(draft.colorPrimary)) { setErr('Primary colour must be a 6-digit hex (e.g. #A5622E).'); return; }
    if (draft.colorSecondary && !HEX.test(draft.colorSecondary)) { setErr('Secondary colour must be a 6-digit hex.'); return; }
    save.mutate({
      tone: draft.tone, audience: draft.audience, voice: draft.voice, bannedPhrases: draft.bannedPhrases,
      defaultCta: draft.defaultCta, contact: draft.contact, colorPrimary: draft.colorPrimary,
      colorSecondary: draft.colorSecondary, fonts: draft.fonts,
    });
  };

  const logo = logoQ.data;

  return (
    <Drawer open={open} onClose={onClose} title="Brand Kit" accent widthClass="w-full max-w-[460px]">
      {q.isLoading ? (
        <LoadingState rows={4} />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : draft ? (
        <div className="space-y-5">
          <p className="text-xs text-text-secondary">
            These values ground the AI studio and auto-apply to every template render. Stored workspace-scoped in
            <code className="ml-1 rounded bg-inset px-1">social.brand.*</code>.
          </p>

          {/* -------- Logo -------- */}
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">Logo</h3>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border-default bg-inset">
                {logo ? (
                  <img src={logo.dataUrl} alt="Brand logo" className="max-h-full max-w-full object-contain" />
                ) : (
                  <span className="px-1 text-center text-[10px] leading-tight text-text-tertiary">Text-mark<br />fallback</span>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={onPickLogo}
                  className="hidden"
                  aria-label="Upload brand logo"
                />
                <div className="flex items-center gap-2">
                  <Button variant="secondary" onClick={() => fileRef.current?.click()} disabled={upload.isPending}>
                    {upload.isPending ? 'Uploading…' : logo ? 'Replace logo' : 'Upload logo'}
                  </Button>
                  {logo && <Badge tone="success">Logo set</Badge>}
                </div>
                <p className="text-[11px] text-text-tertiary">PNG or JPEG, up to 2 MB. Stored workspace-scoped.</p>
              </div>
            </div>
            {logoErr && <p role="alert" className="text-sm text-danger">{logoErr}</p>}
          </section>

          {/* -------- Colours -------- */}
          <section className="space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">Brand colours</h3>
            <ColorWheelPicker label="Primary colour" value={draft.colorPrimary} fallback="#A5622E" onChange={(hex) => set('colorPrimary', hex)} />
            <ColorWheelPicker label="Secondary colour" value={draft.colorSecondary} fallback="#1B1B1A" onChange={(hex) => set('colorSecondary', hex)} />
          </section>

          {/* -------- Fonts -------- */}
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">Approved fonts</h3>
            <div className="max-h-56 space-y-3 overflow-y-auto rounded-md border border-border-subtle p-2">
              {FONT_GROUPS.map((group) => (
                <div key={group} className="space-y-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">{group}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {BRAND_FONTS.filter((f) => f.category === group).map((f) => {
                      const active = draft.fonts.includes(f.family);
                      return (
                        <button
                          key={f.family}
                          type="button"
                          aria-pressed={active}
                          onClick={() => toggleFont(f.family)}
                          className={'flex flex-col items-start rounded-md border px-2 py-1.5 text-left transition-colors ' + (active ? 'border-ai bg-ai/10' : 'border-border-default hover:border-ai/50')}
                        >
                          <span className="text-base leading-tight text-text-primary" style={{ fontFamily: fontStack(f.family) }}>Ag</span>
                          <span className={'text-[11px] ' + (active ? 'text-ai' : 'text-text-secondary')} style={{ fontFamily: fontStack(f.family) }}>{f.family}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-text-tertiary">Licence-clean, self-hosted (OFL) fonts served from our own domain — never a Google CDN. Select one or more.</p>
          </section>

          {/* -------- Voice / text -------- */}
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-text-secondary">Brand voice</span>
            <textarea className={areaCls} rows={3} maxLength={500} value={draft.voice} onChange={(e) => set('voice', e.target.value)} placeholder="How the brand sounds — warm, expert, playful…" />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-text-secondary">Generation tone (short control)</span>
            <input className={inputCls} maxLength={300} value={draft.tone} onChange={(e) => set('tone', e.target.value)} placeholder="e.g. warm and encouraging" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-text-secondary">Default CTA</span>
              <input className={inputCls} maxLength={200} value={draft.defaultCta} onChange={(e) => set('defaultCta', e.target.value)} placeholder="Book now" />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-text-secondary">Contact line</span>
              <input className={inputCls} maxLength={200} value={draft.contact} onChange={(e) => set('contact', e.target.value)} placeholder="@yourbrand" />
            </label>
          </div>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-text-secondary">Banned phrases (one per line)</span>
            <textarea className={areaCls} rows={2} value={draft.bannedPhrases.join('\n')} onChange={(e) => set('bannedPhrases', e.target.value.split('\n').map((x) => x.trim()).filter(Boolean))} placeholder="guaranteed" />
          </label>

          {err && <p role="alert" className="text-sm text-danger">{err}</p>}
          {save.isError && <p role="alert" className="text-sm text-danger">Could not save the Brand Kit. Try again.</p>}
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={onClose}>Close</Button>
            <Button variant="ai" onClick={onSave} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save Brand Kit'}</Button>
          </div>
        </div>
      ) : null}
    </Drawer>
  );
}
